import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import api from '../services/api';
import { toast } from 'react-toastify';
import LoadError from '../components/common/LoadError';

// ─── Constants ────────────────────────────────────────────────────────────────
const SORT_OPTIONS = [
  { key: 'trending',    label: 'Trending' },
  { key: 'most-liked',  label: 'Most Liked' },
  { key: 'most-viewed', label: 'Most Viewed' },
  { key: 'newest',      label: 'Newest' },
  { key: 'biggest',     label: 'Biggest' },
];

const CATEGORY_OPTIONS = [
  { key: 'muscle',  label: 'Muscle' },
  { key: 'jdm',     label: 'JDM' },
  { key: 'euro',    label: 'Euro' },
  { key: 'luxury',  label: 'Luxury' },
  { key: 'exotic',  label: 'Exotic' },
  { key: 'classic', label: 'Classic' },
  { key: 'offroad', label: 'Off-road' },
  { key: 'ev',      label: 'EV' },
  { key: 'tuner',   label: 'Tuner' },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

const Avatar = ({ src, username, size = 32 }) => (
  <div style={{
    position: 'relative',
    width: size, height: size, borderRadius: '50%', overflow: 'hidden', flexShrink: 0,
    background: '#2a3142', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: size * 0.4, color: '#8892b0', fontWeight: 700, border: '1px solid rgba(255,255,255,0.08)',
  }}>
    {(username?.[0] || '?').toUpperCase()}
    {src && (
      <img
        src={src} alt="" referrerPolicy="no-referrer"
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
      />
    )}
  </div>
);

// White stroked "+" affordance for the add-vehicle hint (no emoji, not blue)
const PlusIcon = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

// Thin hint shown at the top of the collections list ONLY when the signed-in
// user has zero vehicles. Mirrors the mobile app's "Add your vehicle collection"
// row: avatar + label + a white "+" affordance. Clicking opens the web's
// add-vehicle flow (the user's own garage page, which has the +Add Vehicle CTA).
const AddVehicleHint = ({ user, onClick }) => (
  <div
    onClick={onClick}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 14,
      padding: '12px 16px',
      marginBottom: 16,
      cursor: 'pointer',
      borderRadius: 14,
      background: 'linear-gradient(145deg, rgba(255,255,255,0.16) 0%, rgba(220,225,235,0.09) 60%, rgba(200,210,225,0.12) 100%)',
      border: '1px solid rgba(255,255,255,0.26)',
      backdropFilter: 'blur(28px) saturate(160%) brightness(1.05)',
      WebkitBackdropFilter: 'blur(28px) saturate(160%) brightness(1.05)',
    }}
  >
    <Avatar src={user.profileImageUrl} username={user.username} size={40} />
    <span style={{ flex: 1, fontSize: '0.95rem', fontWeight: 600, color: '#ffffff' }}>
      Add your vehicle collection
    </span>
    <PlusIcon size={22} />
  </div>
);

const VehicleThumb = ({ vehicle, onClick }) => {
  const v = vehicle.vehicle || {};
  const img = vehicle.primaryThumbnailUrl
    || vehicle.images?.find(i => i.isPrimary)?.thumbnailUrl
    || vehicle.images?.[0]?.thumbnailUrl
    || vehicle.primaryImageUrl
    || vehicle.images?.find(i => i.isPrimary)?.imageUrl
    || vehicle.images?.[0]?.imageUrl;
  const label = vehicle.nickname || `${v.year || ''} ${v.make || ''} ${v.model || ''}`.trim() || 'Vehicle';

  return (
    <div className="collection-thumb" onClick={onClick}>
      <div className="collection-thumb-img">
        {img
          ? <img src={img} alt={label} loading="lazy" decoding="async" />
          : <div className="collection-thumb-placeholder">NO PHOTO</div>}
      </div>
      <div className="collection-thumb-label">{label}</div>
    </div>
  );
};

// Heart icon — outline when not liked, filled when liked
const HeartIcon = ({ filled }) => (
  <svg width="16" height="16" viewBox="0 0 24 24"
    fill={filled ? '#ff5577' : 'none'}
    stroke={filled ? '#ff5577' : 'currentColor'}
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
  </svg>
);

const LikeButton = ({ liked, count, onToggle, busy }) => (
  <button
    className={`collection-like-bare${liked ? ' liked' : ''}`}
    onClick={(e) => { e.stopPropagation(); if (!busy) onToggle(); }}
    aria-label={liked ? 'Unlike collection' : 'Like collection'}
    disabled={busy}
  >
    <HeartIcon filled={liked} />
    <span className="collection-like-count">{count}</span>
  </button>
);

// Multi-select dropdown for categories (mirrors LandmarkTagDropdown styling)
const CategoryDropdown = ({ selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const label = selected.length === 0 ? 'All Categories'
    : selected.length === 1 ? CATEGORY_OPTIONS.find(o => o.key === selected[0])?.label
    : `${selected.length} Categories`;

  const toggle = (key) => {
    if (selected.includes(key)) onChange(selected.filter(k => k !== key));
    else onChange([...selected, key]);
  };

  return (
    <div className="route-sort-dropdown" ref={ref}>
      <button className="route-sort-trigger" onClick={() => setOpen(o => !o)}>
        <span className="route-sort-trigger-label">Filter: <strong>{label}</strong></span>
        <svg className={`route-sort-chevron${open ? ' open' : ''}`} width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      {open && (
        <div className="route-sort-menu">
          <button
            className={`route-sort-item${selected.length === 0 ? ' active' : ''}`}
            onMouseDown={() => onChange([])}
          >
            <span className="route-sort-item-label">All Categories</span>
            {selected.length === 0 && (
              <svg className="route-sort-check" width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M2 6l3 3 5-5" stroke="#4a9eff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </button>
          {CATEGORY_OPTIONS.map(opt => {
            const active = selected.includes(opt.key);
            return (
              <button
                key={opt.key}
                className={`route-sort-item${active ? ' active' : ''}`}
                onMouseDown={() => toggle(opt.key)}
              >
                <span className="route-sort-item-label">{opt.label}</span>
                {active && (
                  <svg className="route-sort-check" width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2 6l3 3 5-5" stroke="#4a9eff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Single-select sort dropdown
const SortDropdown = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const active = SORT_OPTIONS.find(o => o.key === value) || SORT_OPTIONS[0];

  return (
    <div className="route-sort-dropdown" ref={ref}>
      <button className="route-sort-trigger" onClick={() => setOpen(o => !o)}>
        <span className="route-sort-trigger-label">Sort: <strong>{active.label}</strong></span>
        <svg className={`route-sort-chevron${open ? ' open' : ''}`} width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      {open && (
        <div className="route-sort-menu">
          {SORT_OPTIONS.map(opt => (
            <button
              key={opt.key}
              className={`route-sort-item${value === opt.key ? ' active' : ''}`}
              onMouseDown={() => { onChange(opt.key); setOpen(false); }}
            >
              <span className="route-sort-item-label">{opt.label}</span>
              {value === opt.key && (
                <svg className="route-sort-check" width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M2 6l3 3 5-5" stroke="#4a9eff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const CollectionRow = ({ collection, onLikeToggle }) => {
  const navigate = useNavigate();
  const { owner, vehicles, isLiked, likeCount } = collection;
  const [busy, setBusy] = useState(false);
  const carsRef = useRef(null);

  useEffect(() => {
    const el = carsRef.current;
    if (!el) return;

    // Click-and-drag horizontal scroll. Tracks pointer movement past a small
    // threshold to distinguish a "drag" from a regular click on a thumbnail —
    // if the user actually dragged, the trailing click is suppressed so they
    // don't accidentally navigate into a vehicle they were just scrolling past.
    let isDown = false;
    let startX = 0;
    let startScrollLeft = 0;
    let moved = false;
    const DRAG_THRESHOLD = 5;

    const onMouseDown = (e) => {
      if (e.button !== 0) return;
      if (el.scrollWidth <= el.clientWidth) return;
      isDown = true;
      moved = false;
      startX = e.pageX;
      startScrollLeft = el.scrollLeft;
      el.classList.add('is-dragging');
      e.preventDefault();
    };
    const onMouseMove = (e) => {
      if (!isDown) return;
      const dx = e.pageX - startX;
      if (!moved && Math.abs(dx) > DRAG_THRESHOLD) moved = true;
      if (moved) el.scrollLeft = startScrollLeft - dx;
    };
    const onMouseUp = () => {
      if (!isDown) return;
      isDown = false;
      el.classList.remove('is-dragging');
    };
    const onClickCapture = (e) => {
      if (moved) {
        e.preventDefault();
        e.stopPropagation();
        moved = false;
      }
    };

    el.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    el.addEventListener('click', onClickCapture, true);

    return () => {
      el.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      el.removeEventListener('click', onClickCapture, true);
    };
  }, []);

  const toggleLike = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onLikeToggle(collection);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="collection-row">
      <div className="collection-row-owner" onClick={() => navigate(`/profile/${owner.username}`)}>
        <Avatar src={owner.profileImageUrl} username={owner.username} size={56} />
        <div className="collection-row-owner-info">
          <div className="collection-row-username">{owner.username}</div>
          <div className="collection-row-count">{vehicles.length} vehicle{vehicles.length !== 1 ? 's' : ''}</div>
        </div>
      </div>

      <div className="collection-row-cars" ref={carsRef}>
        {vehicles.map(v => (
          <VehicleThumb
            key={v.id}
            vehicle={v}
            onClick={() => navigate(`/vehicle/${v.id}`)}
          />
        ))}
        <div className="collection-row-actions">
          <button
            className={`collection-action-btn like${isLiked ? ' liked' : ''}`}
            onClick={(e) => { e.stopPropagation(); if (!busy) toggleLike(); }}
            disabled={busy}
            aria-label={isLiked ? 'Unlike collection' : 'Like collection'}
          >
            <span className="collection-action-heart-row">
              <HeartIcon filled={!!isLiked} />
              <span className="collection-action-count">{likeCount || 0}</span>
            </span>
            <span>{isLiked ? 'Liked' : 'Like Collection'}</span>
          </button>
          <button
            className="collection-action-btn view"
            onClick={() => navigate(`/garage/${owner.id}`)}
          >
            <span>View Garage</span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Collections Page ────────────────────────────────────────────────────
const Collections = () => {
  const navigate = useNavigate();
  const currentUser = useSelector(s => s.auth?.user);
  const [searchParams, setSearchParams] = useSearchParams();
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  // null = unknown (don't show hint), false = confirmed zero vehicles (show hint)
  const [hasVehicles, setHasVehicles] = useState(null);

  // Does the signed-in user have any vehicles? Drives the hint at the top.
  // Mirrors mobile: GET /garages/:id/value returns a vehicleCount. Default to
  // "has vehicles" on any uncertainty so we never nag an existing collector.
  useEffect(() => {
    if (!currentUser?.id) { setHasVehicles(null); return; }
    let cancelled = false;
    api.get(`/garages/${currentUser.id}/value`)
      .then(({ data }) => { if (!cancelled) setHasVehicles((data?.vehicleCount ?? 1) > 0); })
      .catch(() => { if (!cancelled) setHasVehicles(true); });
    return () => { cancelled = true; };
  }, [currentUser?.id]);

  const sort = searchParams.get('sort') || 'trending';
  const categories = useMemo(() => {
    const raw = searchParams.get('categories');
    return raw ? raw.split(',').filter(Boolean) : [];
  }, [searchParams]);

  // Sync filter state → URL
  const updateFilters = (next) => {
    const params = {};
    const nextSort = next.sort ?? sort;
    const nextCats = next.categories ?? categories;
    if (nextSort !== 'trending') params.sort = nextSort;
    if (nextCats.length > 0) params.categories = nextCats.join(',');
    setSearchParams(params, { replace: true });
  };

  // Fetch on filter change
  useEffect(() => {
    setLoading(true);
    setLoadError(false);
    const params = new URLSearchParams();
    params.set('sort', sort);
    if (categories.length > 0) params.set('categories', categories.join(','));
    api.get(`/discover/collections?${params}`)
      .then(({ data }) => setCollections(data.collections || []))
      // An inline error beats a toast here: the toast vanishes and leaves the
      // page still claiming no collections matched.
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, [sort, categories, reloadKey]);

  const handleLikeToggle = async (col) => {
    const ownerId = col.owner.id;
    const wasLiked = !!col.isLiked;
    // Optimistic update
    setCollections(prev => prev.map(c =>
      c.owner.id === ownerId
        ? { ...c, isLiked: !wasLiked, likeCount: (c.likeCount || 0) + (wasLiked ? -1 : 1) }
        : c
    ));
    try {
      if (wasLiked) await api.delete(`/garages/${ownerId}/like`);
      else await api.post(`/garages/${ownerId}/like`);
    } catch (err) {
      // Roll back
      setCollections(prev => prev.map(c =>
        c.owner.id === ownerId
          ? { ...c, isLiked: wasLiked, likeCount: (c.likeCount || 0) + (wasLiked ? 1 : -1) }
          : c
      ));
      toast.error(err.response?.data?.message || 'Could not update like');
    }
  };

  return (
    <div className="container page">
      <div className="collections-filter-bar">
        <CategoryDropdown
          selected={categories}
          onChange={(next) => updateFilters({ categories: next })}
        />
        <SortDropdown
          value={sort}
          onChange={(next) => updateFilters({ sort: next })}
        />
      </div>

      {hasVehicles === false && currentUser && (
        <AddVehicleHint
          user={currentUser}
          onClick={() => navigate(`/garage/${currentUser.id}`)}
        />
      )}

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
          <div className="loading-spinner" />
        </div>
      ) : loadError ? (
        <LoadError
          title="Couldn’t load collections"
          message="We couldn’t reach the server. Check your connection and try again."
          onRetry={() => setReloadKey((k) => k + 1)}
        />
      ) : collections.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">—</div>
          <h3>No collections match these filters</h3>
          <p style={{ color: '#8892b0' }}>Try removing a category or changing the sort.</p>
        </div>
      ) : (
        <div className="collections-list">
          {collections.map(col => (
            <CollectionRow
              key={col.owner.id}
              collection={col}
              onLikeToggle={handleLikeToggle}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Collections;
